import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'

import { chatRoomsAtom, lastSyncTimestampsAtom } from './AtomChats'
import { connectionStateAtom } from './AtomChats'
import ChatService from '../../services/ChatService'

/**
 * Hook to automatically resync chat messages when WebSocket reconnects
 *
 * This hook:
 * 1. Listens for WebSocket connection state changes
 * 2. When connection becomes 'connected' (authenticated and ready) after a disconnect, triggers resync for all active rooms
 * 3. Uses lastSyncTimestamps to determine which messages to fetch (only new messages since last sync)
 * 4. Provides a quickResync function to manually trigger resync (e.g., when app returns from background)
 *
 * Usage: Add this hook to ChatPage or wherever chat is initialized
 * @returns {Function} quickResync - Function to manually trigger quick resync for active rooms
 */
export function useChatResync() {
  const connectionState = useAtomValue(connectionStateAtom)
  const chatRooms = useAtomValue(chatRoomsAtom)
  const lastSyncTimestamps = useAtomValue(lastSyncTimestampsAtom)

  // Track previous connection state to detect reconnects
  const lastConnectionStateRef = useRef(connectionState)
  // Track if this is the initial connection (don't resync on first connect)
  const isInitialConnectionRef = useRef(true)

  useEffect(() => {
    const wasConnected = lastConnectionStateRef.current === 'connected'
    const isConnected = connectionState === 'connected'
    const wasDisconnected = lastConnectionStateRef.current === 'disconnected' || lastConnectionStateRef.current === 'error'

    // On first connection, mark as initial and skip resync
    if (isConnected && isInitialConnectionRef.current) {
      isInitialConnectionRef.current = false
      lastConnectionStateRef.current = connectionState
      return
    }

    // Trigger resync when reconnecting (transitioning from disconnected/error to connected)
    if (wasDisconnected && isConnected && !wasConnected) {
      // Find rooms that have messages loaded (active rooms)
      // Check if room has messages by trying to get the atom (if it exists, messages were loaded)
      const activeRoomIds = chatRooms
        .filter((room) => {
          // Only resync rooms that have been loaded before (have lastSyncTimestamp)
          // We can't easily check if messages exist without accessing the atom, so we rely on lastSyncTimestamp
          return lastSyncTimestamps[room.id]
        })
        .map((room) => room.id)

      if (activeRoomIds.length > 0) {
        console.log('WebSocket reconnected, triggering resync for rooms:', activeRoomIds)

        activeRoomIds.forEach((roomId) => {
          try {
            // Get last sync timestamp for this room (if any)
            // This ensures we only fetch new messages since last sync
            const since = lastSyncTimestamps[roomId] || null

            // Send resync request via WebSocket
            ChatService.resync(roomId, since, 50)
          } catch (error) {
            console.error(`Failed to resync room ${roomId}:`, error)
          }
        })
      }
    }

    lastConnectionStateRef.current = connectionState
  }, [connectionState, chatRooms, lastSyncTimestamps])

  /**
   * Quick resync function to manually trigger resync for active rooms
   * Useful when app returns from background (e.g., on iPhone)
   */
  const quickResync = useCallback(() => {
    // Only resync if WebSocket is connected
    if (connectionState !== 'connected') {
      console.log('WebSocket not connected, skipping quick resync')
      return
    }

    // Find rooms that have messages loaded (active rooms)
    const activeRoomIds = chatRooms
      .filter((room) => {
        // Only resync rooms that have been loaded before (have lastSyncTimestamp)
        return lastSyncTimestamps[room.id]
      })
      .map((room) => room.id)

    if (activeRoomIds.length > 0) {
      console.log('Quick resync triggered for rooms:', activeRoomIds)

      activeRoomIds.forEach((roomId) => {
        try {
          // Get last sync timestamp for this room (if any)
          // This ensures we only fetch new messages since last sync
          const since = lastSyncTimestamps[roomId] || null

          // Send resync request via WebSocket
          ChatService.resync(roomId, since, 50)
        } catch (error) {
          console.error(`Failed to quick resync room ${roomId}:`, error)
        }
      })
    }
  }, [connectionState, chatRooms, lastSyncTimestamps])

  return quickResync
}

export default useChatResync
